/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable react/destructuring-assignment */
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardHeader,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import Drawer, { DrawerProps } from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import makeStyles from "@mui/styles/makeStyles";
import React, { useCallback, useState } from "react";
import { Choose, For, If } from "react-extras";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { AccessEnum } from "~/api/GeneratedApi";
import InseaLogo from "~/assets/img/insea-no-text.svg";
import { useRoutes } from "~/routes";
import { useAccess } from "~/utils/hooks/useAccess";
import useAuth from "~/utils/hooks/useAuth";
import NavigatorButton, { IsLink } from "./NavigatorButton";
import NavigatorCollapse from "./NavigatorCollapse";

export interface MainMenuCategory {
  id: string;
  children: MainMenuItemType[];
  translation: string;
}

type MainMenuItemType =
  | MainMenuItem
  | MainMenuButton
  | MainMenuLink
  | MainMenuCollapse;

export interface MainMenuItem {
  id: string;
  icon: JSX.Element;
  translation: string;
  isActive?: () => boolean;
  requiredAccess?: AccessEnum | AccessEnum[];
  hidden?: boolean;
}

export interface MainMenuButton extends MainMenuItem {
  onClick: () => void;
}

export interface MainMenuLink extends MainMenuItem {
  to: string;
}

export interface MainMenuCollapse extends MainMenuItem {
  children: MainMenuItemType[];
}

function IsCollapse(item: MainMenuItem): item is MainMenuCollapse {
  return "children" in item;
}

const useStyles = makeStyles((theme) => ({
  categoryHeader: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  categoryHeaderPrimary: {
    color: theme.palette.common.white,
  },
  item: {
    paddingTop: 1,
    paddingBottom: 1,
    color: theme.palette.primary.contrastText,
    "&:hover,&:focus": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
  },
  itemCategory: {
    // backgroundColor: "#232f3e",
    backgroundColor: theme.palette.primary.main,
    boxShadow: "0 -1px 0 #404854 inset",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  firebase: {
    fontSize: 24,
    color: theme.palette.common.white,
    flexDirection: "column",
  },
  itemPrimary: {
    fontSize: "inherit",
  },
  itemIcon: {
    minWidth: "auto",
    marginRight: theme.spacing(2),
  },
  divider: {
    marginTop: theme.spacing(2),
  },
  drawer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  image: {
    width: "80%",
    alignSelf: "center",
    marginBottom: "15px",
    transition: "filter 200ms linear",
    "&:hover": {
      filter: "drop-shadow( 0px 0px 6px rgba(255,255,255,.6))",
      transition: "filter 500ms ease-in",
    },
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  whiteIcon: {
    color: theme.palette.primary.contrastText,
  },
  logoutItem: {
    paddingTop: "8px",
    paddingBottom: "8px",
  },
  arrowIcon: {
    transform: "rotate(180deg)",
    color: theme.palette.primary.contrastText,
  },
  userCard: {
    backgroundColor: theme.palette.primary.semiLight,
    display: "flex",
    margin: "0 16px 0 16px",
  },
  userCardHeader: {
    paddingRight: 2,
  },
  userCardActions: {
    paddingLeft: 0,
  },
  userLogout: {
    alignSelf: "center",
    marginTop: 0,
    marginRight: 0,
  },
  userText: {
    color: theme.palette.primary.contrastText,
  },
  popupMenu: {
    backgroundColor: theme.palette.primary.main,
  },
}));

export const useSharedStyles = makeStyles((theme) => ({
  item: {
    paddingTop: "5px",
    paddingBottom: "5px",
    color: theme.palette.primary.contrastText,
    "&:hover,&:focus": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
  },
  itemActiveItem: {
    color: theme.palette.secondary.light,
    borderColor: theme.palette.secondary.light,
    borderLeft: "2px solid",
  },
  itemPrimary: {
    fontSize: "15px",
  },
  itemIcon: {
    minWidth: "auto",
    marginRight: theme.spacing(2),
    "& .MuiSvgIcon-root": {
      fontSize: "24px",
    },
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  itemText: {
    marginTop: "2px",
    marginBottom: "2px",
  },
}));

function RenderItem(
  item: MainMenuItem,
  nestLevel: number,
  minified = false,
  isInCollapseAndMinified = false,
) {
  if (IsCollapse(item)) {
    const children = item.children
      .filter((c) => !c.hidden ?? true)
      .map((c) =>
        RenderItem(
          c,
          isInCollapseAndMinified || minified ? 1 : nestLevel + 1,
          false,
          minified || isInCollapseAndMinified,
        ),
      );

    const childIsActive = item.children.some(
      (x) => IsLink(x) && window.location.pathname === x.to,
    );

    return (
      <NavigatorCollapse
        key={item.id}
        item={item}
        nestLevel={nestLevel}
        minified={minified}
        isInCollapseAndMinified={isInCollapseAndMinified}
        defaultOpen={childIsActive}
      >
        {children}
      </NavigatorCollapse>
    );
  }
  const { id } = item;
  return (
    <NavigatorButton
      key={id}
      item={item}
      nestLevel={nestLevel}
      minified={minified}
    />
  );
}

const MINIFIED_WIDTH = 57;
const NORMAL_WIDTH = 256;

const Navigator = (
  props: Omit<DrawerProps, "classes"> & {
    onSizeChange: (newSize: number) => void;
  },
): JSX.Element => {
  const { onSizeChange, ...other } = props;
  const classes = useStyles();
  const sharedClasses = useSharedStyles();
  const { t } = useTranslation("template");
  const [minified, setMinified] = useState<boolean>(false);
  const { user, logout } = useAuth();
  const { hasAccess } = useAccess();
  const history = useHistory();
  const { MainMenuItems } = useRoutes();
  const { variant } = props;

  React.useEffect(() => {
    if (variant === "temporary") {
      onSizeChange(0);
      return;
    }
    onSizeChange(minified ? MINIFIED_WIDTH : NORMAL_WIDTH);
  }, [onSizeChange, minified, variant]);

  const [userMenuAnchor, setUserMenuAnchor] =
    React.useState<null | HTMLElement>(null);

  const handleAvatarClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const navigateToHome = useCallback(
    () =>
      history.push({
        pathname: "/map",
      }),
    [history],
  );

  return (
    <Drawer
      variant="permanent"
      classes={{ paper: classes.drawer }}
      PaperProps={{
        style: { width: minified ? MINIFIED_WIDTH : NORMAL_WIDTH },
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: minified ? MINIFIED_WIDTH : NORMAL_WIDTH,
        height: "var(--full-vh, 100vh)",
      }}
      {...other}
    >
      <Box
        sx={{
          backgroundColor: "primary.main",
          boxShadow: "0 -1px 0 #404854 inset",
          paddingTop: 2,
          paddingBottom: 2,
        }}
      >
        <If condition={!minified}>
          <Box
            sx={{
              marginBottom: "16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              onClick={navigateToHome}
              sx={{
                width: "100px",
                height: "100px",
                cursor: "pointer",
                userSelect: "none",
              }}
              draggable={false}
              component="img"
              src={InseaLogo}
            />
            <Typography
              onClick={navigateToHome}
              variant="body2"
              sx={{
                fontSize: 24,
                color: "common.white",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              {t("general:page-title", {
                isDevText:
                  process.env.NODE_ENV !== "production" ? " - DEV" : "",
              })}
            </Typography>
          </Box>
        </If>

        <Choose>
          <Choose.When condition={!minified}>
            <Card classes={{ root: classes.userCard }} elevation={1}>
              <CardActionArea component={RouterLink} to="/settings/profile">
                <CardHeader
                  className={classes.userCardHeader}
                  avatar={<Avatar src="" />}
                  disableTypography
                  title={
                    <Tooltip title={user?.email ?? ""}>
                      <Typography variant="body2" className={classes.userText}>
                        {!user ? <Skeleton width={80} /> : user?.username}
                      </Typography>
                    </Tooltip>
                  }
                  subheader={
                    <Typography variant="body2" className={classes.userText}>
                      {!user ? (
                        <Skeleton width={80} />
                      ) : (
                        user?.organization.name
                      )}
                    </Typography>
                  }
                  classes={{ action: classes.userLogout }}
                />
              </CardActionArea>
              <CardActions className={classes.userCardActions}>
                <Tooltip title={t("Logout").toString()}>
                  <IconButton
                    aria-label="delete"
                    className={classes.whiteIcon}
                    onClick={logout}
                    size="large"
                  >
                    <ExitToAppIcon />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Choose.When>
          <Choose.When condition={minified}>
            <IconButton onClick={handleAvatarClick} size="large">
              <Avatar src="" />
            </IconButton>
            <Menu
              disableAutoFocusItem
              classes={{ paper: classes.popupMenu }}
              open={Boolean(userMenuAnchor)}
              anchorEl={userMenuAnchor}
              onClose={handleUserMenuClose}
            >
              <MenuItem
                className={classes.item}
                onClick={handleUserMenuClose}
                component={RouterLink}
                to="/settings/profile"
              >
                {t("navigation:profilesettings")}
              </MenuItem>
              <MenuItem
                className={classes.item}
                onClick={() => {
                  handleUserMenuClose();
                  void logout();
                }}
              >
                {t("Logout")}
              </MenuItem>
            </Menu>
          </Choose.When>
        </Choose>
      </Box>
      <List disablePadding sx={{ overflowY: "auto", height: "100%" }}>
        <For
          of={MainMenuItems}
          render={({ id, children, translation }: MainMenuCategory) => (
            <React.Fragment key={id}>
              <If condition={!minified}>
                <ListItem className={classes.categoryHeader}>
                  <ListItemText
                    classes={{
                      primary: classes.categoryHeaderPrimary,
                    }}
                  >
                    {t(`navigation:${translation}`)}
                  </ListItemText>
                </ListItem>
              </If>
              {children.map((item) => {
                const { requiredAccess } = item;
                if (requiredAccess && !hasAccess(requiredAccess)) return null;
                return RenderItem(item, 1, minified);
              })}
              <Divider className={classes.divider} />
            </React.Fragment>
          )}
        />
      </List>
      <Choose>
        <Choose.When condition={minified}>
          <ListItem
            key={6}
            button
            className={sharedClasses.item}
            onClick={() => {
              setMinified(!minified);
            }}
          >
            <Tooltip title={t("enlarge").toString()}>
              <ListItemIcon className={sharedClasses.itemIcon}>
                <DoubleArrowIcon />
              </ListItemIcon>
            </Tooltip>
          </ListItem>
        </Choose.When>
        <Choose.When condition={!minified}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <ListItem
              key={6}
              button
              className={sharedClasses.item}
              onClick={() => {
                setMinified(!minified);
              }}
            >
              <Tooltip title={t(minified ? "enlarge" : "minify").toString()}>
                <ListItemIcon className={sharedClasses.itemIcon}>
                  <DoubleArrowIcon className={classes.arrowIcon} />
                </ListItemIcon>
              </Tooltip>
            </ListItem>
          </div>
        </Choose.When>
      </Choose>
    </Drawer>
  );
};

export default Navigator;
